import { BackgroundCheckConfigByType } from '@/backend/features/background-check/defaults';
import { BackgroundCheckAnalytics, BackgroundCheckScreeningStatus } from '@/backend/features/background-check/shared';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { StatisticsCard } from '@juvare/common-web-ui/StatisticsCard';
import { toKebabCase } from '@juvare/missions/ui/utils/toKebabCase';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { Box, Paper, Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BackgroundCheckStatusCardsConfig, StatusCardConfig } from '../../background-check';

export interface ScreeningsSectionProps {
    analytics: BackgroundCheckAnalytics;
    detailsLink: string;
    statusCardsConfig: BackgroundCheckStatusCardsConfig;
    sectionHeading?: React.ReactNode;
}

export const ScreeningsSection: React.FC<ScreeningsSectionProps> = ({ sectionHeading, statusCardsConfig, analytics, detailsLink }) => {
    const navigate = useNavigate();
    const settings = useSelector(getSettings);
    const { t } = useTranslation();

    const navigateToDetailsTable = (type?: BackgroundCheckType, status?: keyof BackgroundCheckConfigByType['filter']) => {
        const urlParams = new URLSearchParams();
        if (type && status) {
            const statusFilterValue = settings.backgroundCheckConfig.type[type]?.filter[status];
            urlParams.set(
                'filter',
                JSON.stringify({
                    backgroundCheckType: type,
                    backgroundCheckStatus: statusFilterValue,
                }),
            );
        }
        navigate(
            { pathname: detailsLink, search: urlParams.toString() },
            {
                state: {
                    backgroundCheckType: type,
                },
            },
        );
    };

    const getExpiringFooterText = (status: BackgroundCheckScreeningStatus): string | undefined =>
        status === 'clear' && analytics.clearExpiring > 0
            ? t('pages.screenings.expiredOrExpiringSoon', { quantity: analytics.clearExpiring })
            : undefined;

    const statusCardsConfigEntries = Object.entries(statusCardsConfig) as [BackgroundCheckScreeningStatus, StatusCardConfig][];

    return (
        <Paper
            elevation={0}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid ' + themeColors.border.main,
                borderRadius: 2,
            }}
            data-test={`${toKebabCase(analytics.type)}-background-checks-section`}
        >
            {sectionHeading}
            <Stack gap={2} direction='row' sx={{ p: 2, width: '100%', flexWrap: 'wrap' }}>
                {statusCardsConfigEntries.map(([status, config]) => (
                    <Box
                        key={status}
                        sx={{
                            flex: {
                                xs: '1 100%',
                                sm: '1 calc(50% - 8px)',
                                lg: '1 auto',
                            },
                        }}
                    >
                        <StatisticsCard
                            icon={config.icon}
                            headingColor={config.headingColor}
                            headingText={config.headingText}
                            content={analytics[status]}
                            footerText={getExpiringFooterText(status)}
                            data-test={config['data-test']}
                            onClick={() => {
                                navigateToDetailsTable(analytics.type, status);
                            }}
                        />
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
};
